import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button } from "../../../Components/Buttons";
import {
  FormImage,
  FormInput,
  FormSection,
  FormSelect,
  FormSwitch,
} from "../../../Components/Form";
import { formatDateOnly, formattedDate, ToastMessage, toOptions, useDebouncedCallback } from "../../../Components/Utils";
import BreadCrumb from "../../../Components/BreadCrumb";
import { ROUTE_PATHS } from "../../../constants/routeConstants";
import { user_profile_image_url } from "../../../Components/ImageUrls";
import { handleError } from "../../../stores/components/handleError";
import { useFetchRolesAllQuery } from "../../../stores/api/roles.api";
import {
  useFetchUnitsAllQuery
} from "../../../stores/api/units.api";
import patientReferralHelper from "../../../stores/api/helpers/patient.referral.helper";
import { useFetchAllDistrictsQuery } from "../../../stores/api/district.api";
import { Loader2, SearchIcon } from "lucide-react";
import { useFetchPatientByTrackingMutation, useFetchPhysiciansAllQuery } from "../../../stores/api/patient.api";
import { PatientSearchHelper } from "../../../stores/api/helpers/patients.helper";
import { patientDetailsSchema } from "./Components/Schemas";
import { useCreatePatientReferralMutation, useUpdatePatientReferralMutation } from "../../../stores/api/patient.referrel.api";
import { PatientProfile } from "../../../Components/PatientProfile";

const CreateReferral = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const patientReferralDetail = location?.state?.patient_referral;
  
  const { data: unitRows = [] } = useFetchUnitsAllQuery();
  const units = useMemo(
    () => (unitRows || []).map((r) => ({ label: r.name, value: r.id})),
    [unitRows],
  );


  const [createPatientReferral, { isLoading: creating }] = useCreatePatientReferralMutation();
  const [updatePatientReferral, { isLoading: updating }] = useUpdatePatientReferralMutation();
  const [searchPatient] = useFetchPatientByTrackingMutation();
  const loading = creating || updating;

  const [formData, setFormData] = useState({
    patient_id: patientReferralDetail?.patient_id || "",
    unit_id: patientReferralDetail?.unit_id || "",
    physician_id: patientReferralDetail?.physician_id || "",
    hospital_name: patientReferralDetail?.hospital_name || "",
    referred_location: patientReferralDetail?.referred_location ?? "",
    first_aid_given: patientReferralDetail?.first_aid_given || "",
    cause_of_referral: patientReferralDetail?.cause_of_referral || "",
    status: patientReferralDetail?.status ? true : false,
  });
  const [searching, setSearching] = useState(false);
  const [patientDetails, setPatientDetails] = useState(patientDetailsSchema)
  const [unitId, setUnitId] = useState(
    formData.unit_id ?? patientDetails.unit_id
  )
  const { data: unitPhysicians } = useFetchPhysiciansAllQuery(unitId, {
    skip: !unitId,
    refetchOnMountOrArgChange: true,
  })
  const physicians = useMemo(() => toOptions(unitPhysicians), [unitPhysicians]);

  const setField = (name, value) => {
    if (name == "unit_id") {
      setUnitId(value)
    }
    setFormData((prev) => ({ ...prev, [name]: value }));
  }
  useEffect(() => {
    if(patientReferralDetail) {
      const tracking_number =  patientReferralDetail?.patient?.system_number || patientReferralDetail?.patient?.patient_cnic || patientReferralDetail?.patient?.prescription_pad_no;
      setField("tracking_number", tracking_number)
      handlePatientSearch(tracking_number);
    }
  } , [])
  const handlePatientSearch = async (tracking_number = null) => {
    setSearching(true);
    try {
      const result = await new Promise((resolve) => {
        setTimeout(() => {
          resolve(
            searchPatient(
              PatientSearchHelper.forCreate({
                tracking_number: formData.tracking_number || tracking_number || "",
              })
            ).unwrap()
          );
        }, 300);
      });
      const patient = result?.patient;
      setField("patient_id", patient?.id)
      setPatientDetails({
        patient_name: patient?.patient_name || "",
        father_husband_name: patient?.father_husband_name || "",
        patient_type: patient?.patient_type || "",
        gender: patient?.gender || "",
        system_number: patient?.system_number || "",
        dob: patient?.dob || "",
        age: patient?.age || "",
        mobile: patient?.mobile || "",
        patient_cnic: patient?.patient_cnic || "",
        unit_name: patient?.unit?.name || "",
        district_name: patient?.unit?.district?.name || "",
      });
    } catch (error) {
      handleError(error, "Something went wrong.");
    } finally {
      setSearching(false);
    }
  };
  const resetForm = () =>
    setFormData({
      patient_id:"",
      unit_id:"",
      physician_id:"",
      hospital_name:"",
      referred_location:"",
      first_aid_given:"",
      status:"",
      cause_of_referral:"",
    });

  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      if (patientReferralDetail) {
        console.log(typeof formData.status);
        await updatePatientReferral(
          patientReferralHelper.forUpdate(patientReferralDetail.id, formData),
        ).unwrap();
      } else {
        console.log(formData)
        await createPatientReferral(patientReferralHelper.forCreate(formData)).unwrap();
      }
      ToastMessage({
        type: "success",
        title: "Operation completed successfully.",
        message: patientReferralDetail
          ? "Patient Referral updated successfully!"
          : "Patient Referral created successfully!",
      });
      if (!patientReferralDetail) resetForm();
    } catch (err) {
      handleError(err, "Something went wrong");
    }
  };

  const patientStatus = formData?.status || "OPD";

  return (
    <div className="p-4">
      <BreadCrumb title={patientReferralDetail ? "Edit Patient Referral" : "Create Patient Referral"} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2">
          <form className="mx-auto mt-6 dark:bg-slate-900 rounded-lg p-6 space-y-5">
            <FormSection title=" Patient Refferal Form">
              <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
                <FormSection className="pt-3">
                  <div>
                    <label className="block text-sm font-medium mb-2">
                      Patient <span className="text-red-500">*</span>
                    </label>

                    <div className="flex overflow-hidden rounded-md border border-gray-300 bg-white focus-within:ring-2 focus-within:ring-green-500">
                      <input
                        type="text"
                        placeholder="System Number, Prescription Pad No, or CNIC"
                        value={formData.tracking_number}
                        onChange={(e) => setField("tracking_number", e.target.value)}
                        className="flex-1 px-3 py-[8px] outline-none border-none"
                      />
                      <button
                        type="button"
                        onClick={handlePatientSearch}
                        disabled={searching}
                        className="px-5 bg-green-600 text-white hover:bg-green-700 transition"
                      >
                        {searching ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <SearchIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>

                    <p className="mt-2 text-xs text-gray-500">
                      Search using the patient's System Number, Prescription Pad Number, or CNIC.
                    </p>
                  </div>
                </FormSection>

                <FormSection title="" className="pt-3">
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-4">
                    <FormSelect
                      label=" Unit"
                      isRequired
                      options={units}
                      value={Number(formData.unit_id)}
                      onChange={(val) => setField("unit_id", val ?? "")}
                      blurInputOnSelect
                    />

                    <FormSelect
                      label=" Unit Physician"
                      isRequired
                      options={physicians}
                      value={formData.physician_id}
                      onChange={(val) => setField("physician_id", val ?? "")}
                      blurInputOnSelect
                    />
                  </div>
                </FormSection>

                {/* 🔥 Referral Details Section - DO NOT MODIFY ABOVE CODE */}
                <FormSection
                  title="Where and why the patient is referred"
                  className="pt-3"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <FormInput
                      label="Hospital Name"
                      isRequired
                      type="text"
                      value={formData?.hospital_name}
                      onChange={(val) => setField("hospital_name", val)}
                      placeholder="Hospital Name"
                    />
                    <FormInput
                      label="Referred Location "
                      isRequired
                      type="text"
                      value={formData?.referred_location}
                      onChange={(val) => setField("referred_location", val)}
                      placeholder="Referred Location "
                    />

                    <FormInput
                      label="First Aid Given "
                      isRequired
                      type="textarea"
                      value={formData?.first_aid_given}
                      onChange={(val) => setField("first_aid_given", val)}
                      placeholder="Write first aid details..."
                      rows={4}
                    />

                    <FormInput
                      label="Cause of Referral"
                      isRequired
                      type="textarea"
                      value={formData?.cause_of_referral}
                      onChange={(val) => setField("cause_of_referral", val)}
                      placeholder="Write reason for referral..."
                      rows={4}
                    />
                  </div>
                </FormSection>
              </div>
            </FormSection>  
              <FormSection title="Status">
            <div className="flex gap-3 items-center">
              <FormSwitch checked={formData.status} onChange={(val) => setField("status", Boolean(val) )} />
            </div>
          </FormSection>             
            <div className="flex items-center justify-end gap-3 pt-2">
              <Button
                type="button"
                onClick={() => {
                  resetForm();
                  navigate(ROUTE_PATHS.REFERREL_LIST);
                }}
                className="!bg-gray-100 !text-gray-700 hover:!bg-gray-200 dark:!bg-slate-800 dark:!text-gray-200"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="!bg-green-500 !text-white hover:!bg-green-600"
                loading={loading}
                disabled={loading}
                onClick={onSubmit}
                loadingText={patientReferralDetail ? "Updating..." : "Creating..."}
              >
                {patientReferralDetail ? "Update Changes" : "Save Changes"}
              </Button>
            </div>
          </form>
        </div>

        <div className="xl:col-span-1">
          <PatientProfile patientDetails={patientDetails}/>
        </div>
      </div>
    </div>
  );
};

export default CreateReferral;
